import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import { Download, PenLine, FileText, FileType, X, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { api, API, apiError } from "@/lib/api";
import { BRAND } from "@/lib/brand";

const CAT_ORDER = ["Client Forms", "Recovery Letters", "Fees & Billing", "Court Templates"];

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(null);

  useEffect(() => {
    api.get("/documents?public=true").then((r) => {
      setDocs(r.data.documents);
      setLoading(false);
    });
    window.scrollTo(0, 0);
  }, []);

  const download = (slug) => {
    window.open(`${API}/documents/${slug}/pdf`, "_blank");
  };

  const downloadDocx = (slug) => {
    window.open(`${API}/documents/${slug}/docx`, "_blank");
  };

  const grouped = CAT_ORDER.map((c) => ({ cat: c, items: docs.filter((d) => d.category === c) })).filter((g) => g.items.length);

  const PHRASE = { "Client Forms": "forms", "Recovery Letters": "letters", "Fees & Billing": "fees", "Court Templates": "court templates" };
  const phrases = grouped.map((g) => PHRASE[g.cat] || g.cat.toLowerCase());
  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const headLead = phrases.length > 1 ? phrases.slice(0, -1).join(", ") : "";
  const headLast = phrases.length ? phrases[phrases.length - 1] : "documents";
  const hasSignable = docs.some((d) => d.signable);

  return (
    <div className="light-scope">
      <Nav theme="light" />
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 pt-32 md:pt-40 pb-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#58635C] hover:text-[#0A0D0B] transition-colors mb-8">
          <ArrowLeft size={15} /> Back to home
        </Link>
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#B89874]">Document Centre</span>
        <h1 className="font-display text-4xl md:text-6xl tracking-tight text-[#0A0D0B] mt-4 leading-[1.02]">
          {headLead && <>{cap(headLead)} &<br /></>}
          <span className="italic">{headLead ? headLast : cap(headLast)}.</span>
        </h1>
        <p className="mt-6 text-[#58635C] max-w-2xl leading-relaxed">
          {hasSignable
            ? <>Download any document as a PDF, or complete and e-sign your Letter of Engagement online. </>
            : <>Download any document below as a PDF or Word file. </>}
          All documents are prepared by {BRAND.legal}.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#B89874]" /></div>
      ) : (
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 pb-28 space-y-16">
          {grouped.map((g) => (
            <div key={g.cat}>
              <div className="flex items-center gap-4 mb-6">
                <h2 className="font-display text-2xl md:text-3xl text-[#0A0D0B]">{g.cat}</h2>
                <span className="h-px flex-1 bg-[#E5E1D8]" />
                <span className="font-mono-gg text-xs text-[#58635C]">{g.items.length}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {g.items.map((d) => (
                  <div
                    key={d.slug}
                    data-testid={`doc-card-${d.slug}`}
                    className="bg-white border border-[#E5E1D8] p-6 flex flex-col hover:border-[#B89874] transition-colors duration-300"
                  >
                    <div className="flex items-start gap-3">
                      <FileText size={20} className="text-[#B89874] mt-1 shrink-0" strokeWidth={1.6} />
                      <div>
                        <h3 className="font-display text-xl text-[#0A0D0B] leading-snug">{d.title}</h3>
                        <p className="mt-2 text-sm text-[#58635C] leading-relaxed">{d.description}</p>
                      </div>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <button
                        data-testid={`download-${d.slug}`}
                        onClick={() => download(d.slug)}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 border border-[#0A0D0B] text-[#0A0D0B] hover:bg-[#0A0D0B] hover:text-white transition-colors"
                      >
                        <Download size={15} /> PDF
                      </button>
                      <button
                        data-testid={`word-${d.slug}`}
                        onClick={() => downloadDocx(d.slug)}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 border border-[#E5E1D8] text-[#58635C] hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors"
                      >
                        <FileType size={15} /> Word
                      </button>
                      {d.signable && (
                        <button
                          data-testid={`sign-${d.slug}`}
                          onClick={() => setSigning(d)}
                          className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 bg-[#B89874] text-[#0A0D0B] hover:bg-[#C9A780] transition-colors"
                        >
                          <PenLine size={15} /> Complete & Sign
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Footer />
      {signing && <SignModal doc={signing} onClose={() => setSigning(null)} />}
    </div>
  );
}

function SignModal({ doc, onClose }) {
  const sigRef = useRef(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (name) => (e) => setValues((v) => ({ ...v, [name]: e.target.value }));

  const submit = async () => {
    for (const f of doc.fields) {
      if (f.required && !values[f.name]) return toast.error(`Please enter ${f.label}`);
    }
    if (!sigRef.current || sigRef.current.isEmpty()) return toast.error("Please add your signature");
    setBusy(true);
    try {
      const signature = sigRef.current.toDataURL("image/png");
      const { data } = await api.post("/submissions", { slug: doc.slug, fields: values, signature });
      toast.success("Signed and submitted.");
      setResult(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-4 bg-[#0A0D0B]/70 backdrop-blur-sm overflow-y-auto">
        <div className="bg-white w-full max-w-lg border border-[#E5E1D8] my-8" data-testid="sign-success">
          <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-[#E5E1D8]">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#B89874]">Engagement received</div>
              <h3 className="font-display text-2xl text-[#0A0D0B]">Thank you</h3>
            </div>
            <button onClick={onClose} data-testid="success-close" className="p-2 text-[#58635C] hover:text-[#0A0D0B]"><X size={20} /></button>
          </div>
          <div className="px-6 md:px-8 py-7">
            <div className="flex items-start gap-3">
              <CheckCircle2 size={22} className="text-[#3F6B4E] mt-0.5 shrink-0" />
              <p className="text-sm text-[#233027] leading-relaxed">
                Your signed <b>{doc.title}</b> has been submitted. A copy has been emailed to our office and to you.
                Please quote your reference number in any correspondence.
              </p>
            </div>
            <div className="mt-6 bg-[#F5F3EC] border border-[#E5E1D8] px-6 py-5 text-center">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#58635C]">Your reference</div>
              <div data-testid="success-reference" className="mt-1.5 font-mono-gg text-2xl font-semibold text-[#0A0D0B] tracking-wider">{result.reference}</div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={`${API}/submissions/${result.id}/pdf`} target="_blank" rel="noreferrer" data-testid="success-download-pdf"
                className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors">
                <Download size={15} /> Download PDF
              </a>
              <a href={`${API}/submissions/${result.id}/docx`} target="_blank" rel="noreferrer" data-testid="success-download-docx"
                className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-6 py-3 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors">
                <FileType size={15} /> Word
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-4 bg-[#0A0D0B]/70 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white w-full max-w-2xl border border-[#E5E1D8] my-8" data-testid="sign-modal">
        <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-[#E5E1D8]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#B89874]">Complete & e-sign</div>
            <h3 className="font-display text-2xl text-[#0A0D0B]">{doc.title}</h3>
          </div>
          <button onClick={onClose} data-testid="sign-close" className="p-2 text-[#58635C] hover:text-[#0A0D0B]"><X size={20} /></button>
        </div>
        <div className="px-6 md:px-8 py-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {doc.fields.map((f) => (
              <div key={f.name} className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">
                  {f.label}{f.required && <span className="text-[#B89874]"> *</span>}
                </label>
                <input
                  data-testid={`field-${f.name}`}
                  type={f.type === "email" ? "email" : "text"}
                  value={values[f.name] || ""}
                  onChange={set(f.name)}
                  className="border border-[#E5E1D8] px-3.5 py-2.5 text-[#0A0D0B] focus:border-[#B89874] outline-none transition-colors"
                />
              </div>
            ))}
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Signature *</label>
            <div className="mt-1.5 border border-[#E5E1D8] bg-[#FDFCF8]">
              <SignatureCanvas
                ref={sigRef}
                penColor="#0A0D0B"
                canvasProps={{ className: "w-full h-40", "data-testid": "signature-pad" }}
              />
            </div>
            <button
              onClick={() => sigRef.current && sigRef.current.clear()}
              data-testid="signature-clear"
              className="mt-2 text-xs text-[#58635C] hover:text-[#0A0D0B] underline"
            >
              Clear signature
            </button>
          </div>
          <p className="text-xs text-[#58635C] leading-relaxed">
            By signing you agree to engage {BRAND.legal} and accept the Schedule of Charges. A copy will be emailed to our office and downloaded for your records.
          </p>
        </div>
        <div className="px-6 md:px-8 py-5 border-t border-[#E5E1D8] flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-3 text-sm font-semibold text-[#58635C] hover:text-[#0A0D0B]">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="sign-submit"
            className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-7 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <PenLine size={15} />}
            {busy ? "Submitting…" : "Sign & Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}
